import * as React from 'react';
import { flow, forEach, map, has } from 'lodash';
import { II18nMessages } from '../../../../services/branding/models';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { reduxForm, InjectedFormProps, Field, formValueSelector } from 'redux-form';
import { IOptionMap, IProduct } from '../../../../data/api/models';
import SelectControl from '../../../../components/SelectControl';
import { IQuoteActions, IQuoteState } from 'src/app/services/quote/models';
import { isRequired } from '../../../../util/validators';
import { FaCheck, FaWindowClose, FaCog }from 'react-icons/fa';
import FieldFormControl from '../../../../components/FieldFormControl';
import { connect } from 'react-redux';
import * as Commonmark from 'react-commonmark';
import { bindActionCreators } from 'redux';
import { actions as quoteActions } from '../../../../services/quote/reducer';
import FieldFormError from '../../../../components/FieldFormError';

interface IAdditionalCoverForm extends InjectedFormProps {
  i18n: II18nMessages;
  quote: IQuoteState;
  product: IProduct;
  pristine: boolean;
  submitting: boolean;
  onBack: () => void;
  options: IOptionMap;
  schemeId: string;
  activities: string[];
  renderDefinition(name: string): any;
  actions: {
    quote: IQuoteActions;
  };
}

class AdditionalCoverForm extends React.Component<IAdditionalCoverForm> {
  public componentWillMount() {
    const { product, actions, change, options, schemeId } = this.props;
    const steps = ['tripDetails', 'medicalDeclaration', 'additionalCover'];

    actions.quote.updateSteps(steps);

    forEach(product.metadata.options, (option, key) => {
      if (option.auto_included) {
        change(`options.${key}`, true);
      }

      if (! option.default) {
        return;
      }

      if (! has(options, key)) {
        change(`options.${key}`, option.default);
      }
    });
  }

  private renderListOption = (option, key) => {
    const { renderDefinition } = this.props;

    const list = map(option.items, (item, key) => ({
      label: item,
      value: key,
    }));

    return (
      <div key={key}>
        <Row>
          <Col md={12} lg={6}>
            <div className="question-text">
              <div>{option.question} {renderDefinition(key)}</div>
            </div>
          </Col>
          <Col md={12} lg={6}>
            <Field
              name={`options.${key}`}
              type="select"
              options={list}
              component={SelectControl}
              clearable={false}
              validate={[isRequired]}
            />
          </Col>
        </Row>
      </div>
    );
  }

  private renderBooleanOption = (option, key) => {
    const { renderDefinition } = this.props;

    return (
      <div className="radio-question additional-option" key={key}>
        <Row>
          <Col md={12} lg={8}>
            <div className="question-text">
              <div>
                {option.question} {renderDefinition(key)}
              </div>
            </div>
          </Col>
          <Col md={12} lg={4}>
            {! option.auto_included && (
              <Field
                name={`options.${key}`}
                type="radio"
                value={false}
                complexValue={true}
                className="answer"
                component={FieldFormControl}
                validate={[
                  isRequired,
                ]}
                omitError={true}
              >
                <div className="medical-btn">
                  <FaWindowClose /> No
                </div>
              </Field>
            )}
            <Field
              name={`options.${key}`}
              type="radio"
              value={true}
              complexValue={true}
              className="answer"
              component={FieldFormControl}
              validate={[
                isRequired,
              ]}
              omitError={true}
            >
              <div className="medical-btn">
                <FaCheck /> Yes
              </div>
            </Field>
            <Field
              name={`options.${key}`}
              component={FieldFormError}
              validate={[
                isRequired,
              ]}
            />
          </Col>
        </Row>
      </div>
    );
  }

  private renderOption = (option, key) => {
    switch (option.type) {
      case 'boolean':
        return this.renderBooleanOption(option, key);
      case 'list':
        return this.renderListOption(option, key);
      default:
        return null;
    }
  }

  private transformLinks = (url) => {
    const { product } = this.props;

    if (url === 'hazardous_activities') {
      return product.documents[Object.keys(product.documents)[0]];
    }

    if (url === 'cycle_activity_list') {
      return product.documents[Object.keys(product.documents)[1]];
    }

    if (url === 'recreational_activities_list') {
      return product.documents[Object.keys(product.documents)[0]];
    }

    return url;
  }

  private renderNormal = () => {
    const { i18n, product } = this.props;

    return (
      <div>
        <div className="section">
          {product.metadata.hazardous_activities && product.documents && (
            <Commonmark
              source={product.metadata.hazardous_activities}
              linkTarget="_blank"
              transformLinkUri={this.transformLinks}
            />
          )}
        </div>

        <div className="section">
          {map(product.metadata.options, this.renderOption)}
        </div>
      </div>
    );
  }

  private renderProcycle = () => {
    const { i18n, product } = this.props;

    const activityList = product.metadata.activities.map((name, key) => {
      return {
        label: name,
        value: name,
      };
    });

    return (
      <div>
        <div className="section">
        Please select the cycling activities you will be participating in.
        </div>

        <div className="section">
          {product.metadata.hazardous_activities && product.documents && (
            <Commonmark
              source={product.metadata.hazardous_activities.cycling_activities}
              linkTarget="_blank"
              transformLinkUri={this.transformLinks}
            />
          )}

          <p>
            Depending on the activities you select below, you will
            be automatically offered either Touring or Adventure cover.
          </p>

          <Field
            name="activities"
            options={activityList}
            component={SelectControl}
            clearable={true}
            multi={true}
          />
        </div>
        <div className="section">
          {product.metadata.hazardous_activities && product.documents && (
            <Commonmark
              source={product.metadata.hazardous_activities.recreational_activities}
              linkTarget="_blank"
              transformLinkUri={this.transformLinks}
            />
          )}
        </div>
      </div>
    );
  }

  public render() {
    const { i18n, handleSubmit, onBack, submitting, valid, product, submitFailed } = this.props;

    return (
      <Form onSubmit={handleSubmit}>
        <h3>{i18n.quote.additionalCover.title}</h3>

        {product.productCode === 'PROC' ? this.renderProcycle() : this.renderNormal()}

        <div className="btn-bar">
          <Button
            bsStyle="primary"
            type="button"
            onClick={onBack}
          >
            Back
          </Button>
          <div className="pull-right">
            <Button
              bsStyle="primary"
              type="submit"
              disabled={submitting || (submitFailed && !valid)}
            >
              {submitting ? <FaCog className="fa-spin" /> : 'Next'}
            </Button>
          </div>
        </div>
      </Form>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state) => {
      const options = selector(state, 'options');
      const schemeId = selector(state, 'schemeId');
      const activities = selector(state, 'activities');

      return {
        options,
        schemeId,
        activities,
      };
    },
    (dispatch) => ({
      actions: {
        quote: bindActionCreators({ ...quoteActions }, dispatch),
      },
    }),
  ),
])(AdditionalCoverForm);
